import { ButtonProps as MuiButtonProps } from '@everlutionsk/ui';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import { IconButton as MuiIconButton, SxProps, Theme } from '@mui/material';
import React from 'react';

type ButtonVariants =
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'accentFilled'
  | 'destructive'
  | 'ghost'
  | 'unset';
type ButtonSizes = 'xs' | 's' | 'md' | 'l' | 'xl' | 'xxl';

export interface ButtonProps extends Omit<MuiButtonProps, 'color' | 'size' | 'variant'> {
  readonly variant?: ButtonVariants;
  readonly size?: ButtonSizes;
  readonly icon?: IconName;
}

export function IconButton({
  variant = 'secondary',
  size = 'md',
  icon,
  children,
  sx,
  ...props
}: ButtonProps) {
  const mergedStyles = { ...colorVariants[variant], ...iconSizeVariants[size], ...sx };
  const IconComponent = icon ? iconMap[icon] : null;

  return (
    <MuiIconButton {...props} sx={mergedStyles}>
      {IconComponent ? <IconComponent /> : children}
    </MuiIconButton>
  );
}

const iconMap = {
  add: AddIcon,
  arrowRight: ChevronRightIcon,
  edit: ModeEditOutlineOutlinedIcon,
  close: CloseIcon,
  fileDownload: FileDownloadOutlinedIcon,
  dependencyMap: AccountTreeOutlinedIcon
} as const;

export type IconName = keyof typeof iconMap;

const colorVariants: Record<ButtonVariants, SxProps<Theme>> = {
  primary: {
    color: theme => theme.brandPalette.foreground.neutral,
    backgroundColor: theme => theme.brandPalette.background.secondary,
    '&:hover, &:focus': {
      backgroundColor: theme => theme.brandPalette.background.secondaryHover
    }
  },
  accent: {
    color: theme => theme.brandPalette.foreground.accent,
    backgroundColor: theme => theme.brandPalette.background.secondary,
    transition:
      'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, color 0.2s ease',
    '&:hover, &:focus': {
      backgroundColor: theme => theme.brandPalette.background.accent,
      color: theme => theme.brandPalette.background.neutral,
      transform: 'scale(1.12)',
      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)'
    }
  },
  accentFilled: {
    color: theme => theme.brandPalette.foreground.inverse,
    backgroundColor: theme => theme.brandPalette.background.accent,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
    '&:hover, &:focus': {
      backgroundColor: '#274AB3',
      color: theme => theme.brandPalette.foreground.inverse,
      transform: 'scale(1.12)',
      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)'
    }
  },
  secondary: {
    color: theme => theme.brandPalette.foreground.neutral,
    border: theme => `1px solid ${theme.brandPalette.border.neutral}`,
    backgroundColor: theme => theme.brandPalette.background.neutral,
    '&:hover, &:focus': {
      backgroundColor: theme => theme.brandPalette.background.faded
    }
  },
  destructive: {
    color: theme => theme.brandPalette.foreground.inverse,
    backgroundColor: theme => theme.brandPalette.background.negative,
    '&:hover, &:focus': {
      backgroundColor: theme => theme.brandPalette.background.negativeFaded
    }
  },
  ghost: {
    color: 'neutral.main',
    '&:hover': {
      backgroundColor: theme => theme.brandPalette.background.faded
    },

    '&:disabled': {
      backgroundColor: 'transparent',
      border: 'none'
    }
  },
  unset: {
    color: theme => theme.brandPalette.foreground.infoOffset,
    '&:hover': {
      backgroundColor: 'transparent'
    },

    '&:disabled': {
      backgroundColor: 'transparent',
      border: 'none'
    }
  }
};

const iconSizeVariants: Record<Required<ButtonProps>['size'], SxProps<Theme>> = {
  xs: {
    p: 2,
    borderRadius: theme => theme.radius.full,
    '& svg': { width: '14px', height: '14px' }
  },
  s: {
    p: 3,
    borderRadius: theme => theme.radius.full,
    '& svg': { width: '14px', height: '14px' }
  },
  md: {
    p: 3,
    borderRadius: theme => theme.radius.full,
    '& svg': { width: '16px', height: '16px' }
  },
  l: {
    p: 4,
    borderRadius: theme => theme.radius.full,
    '& svg': { width: '20px', height: '20px' }
  },
  xl: {
    p: 5,
    borderRadius: theme => theme.radius.full,
    '& svg': { width: '24px', height: '24px' }
  },
  xxl: {
    p: 6,
    borderRadius: theme => theme.radius.full,
    '& svg': { width: '24px', height: '24px' }
  }
};
